import { BOSS_BY_ID } from './bosses';
import { BODY_K, RNG_K } from './enemies';
import { TINT } from '../entities/enemy-parts';
/* ============================================================
   進戰場的五隻 BOSS —— 造型以外的那一半
   ============================================================

   `data/bosses.ts` 是 28 隻的**造型欄位表**（機械移植，不要手改）：
   尺寸、剪影、階段文案、`build`/`upd`。那張表**沒有任何戰鬥數值** ——
   原型是檢視台，它只有造型與動畫。

   這個檔補的就是缺的那一半，而且**只補排進時刻表的那幾隻**
   （`data/boss-roster.ts`）。沒排進去的 23 隻仍然只活在圖鑑裡。

   ## 血量的軸是「這一場預計打多久」

   `.claude/notes/boss-fight-design.md` 訂的判準：血量與預計時長要一起訂，
   兩個數字綁在一起看。所以每一隻都有 `want`（秒），而 `hp` 是照那個目標
   反推之後**用 `flow.test.ts` 的實測擊殺時間校準**的 —— 不是估的。

   ⚠️ **校準用的 bot 是無敵的、而且會滿等**（20 分鐘打到 74 級、七萬擊殺），
   所以它量到的時間是**下限**，不是玩家會經歷的時間。判準訂成
   「bot 的時間 ≈ `want` 的一半」：真人會被迫走位、會躲、火力也沒那麼滿。
   `flow.test.ts` 每次都會把實測值與 `want` 並排印出來，改血量時看那一行。

   2026-08-03 第一輪實測（bot）：walze 14.9s／zenit 25.5s／mutter 16.9s／
   kathedrale 105.2s／kaiser 37.4s。除了 KATHEDRALE 之外全部太快，
   而使用者的回饋也是「Boss 有點太弱」—— 所以除了牠以外整批上調。

   調完第二輪：walze 15.7／zenit 3.8／mutter 28.6／kathedrale 12.1／
   kaiser **未擊破**。⚠️ **單場的數字變異極大**（同一隻 zenit 兩輪差了七倍），
   因為 Boss 的擊殺時間幾乎完全由**踉蹌窗口串不串得起來**決定
   （未踉蹌 ×0.45、踉蹌 ×3.5，差 7.8 倍 —— `sim/impact.ts` 刻意設計的）。
   所以這張表**不要用單場數字去追**；要調的話跑多顆種子看中位數
   （難度量測的通則，`.claude/rules/lessons.md` 2026-08-02 記過）。
   ⚠️ **KAISER 那個「未擊破」是 bot 的行為造成的，不是血量太高。**
   逐秒追過（`dist`／`hp` 對照）：前五秒貼身打掉 20%，之後 bot 就跑開了，
   整場距離在 10–33 之間擺盪，只是偶爾掃到。牠的移速 4.2 追不上玩家的 11.5，
   所以**傷害量完全取決於玩家要不要留下來打** —— bot 不會主動留。
   真人想打的話，照 KATHEDRALE 那一場的貼身輸出（約 2000/秒）推估 30 秒內解決。
   血量仍然從 76000 降到 58000：18:30 降臨、20:00 撤離只有 90 秒，
   打不完的最終 Boss 等於這一場沒有結尾，寧可留餘裕。

   ⚠️ Boss 的血量**不吃時間倍率**（`spawn.ts` 對 `T.boss` 一律 hpMul = 1）。
   五隻的強度階梯寫在這張表上，看得到、改得動；散在生成器的公式裡的話，
   「第四隻比第三隻強多少」永遠要靠心算。

   ## 稀釋不是清空

   `dilute` 是 Boss 存活期間的同屏目標數係數（`sim/spawn.ts`）。
   全部都在 0.28–0.38 —— 五場都超過 30 秒，照設計文件都該稀釋而不是清場。
   保留 on-kill、經驗與四色壓力，核心迴圈不能斷。

   ## 弱點是**位置**不是準頭

   支柱四是「火力自動、走位手動」，主砲自己會瞄準心 —— 所以弱點不能靠瞄。
   `parts[].ang` 是**相對機體正面的方位角**（0 = 正面、π = 背面），
   命中點落在那個扇形裡就算打到那個部位。玩家要做的是**繞到那一側**，
   那是走位，不是準頭。判定與獎勵在 `sim/boss-parts.ts`。 */

/** BOSS 專用的速度換算。**不能用小怪那個 `SPD_K`（2.3）。**

    實看抓到的：用 2.3 的話 WALZE 是 3.45、KATHEDRALE 是 1.61，
    而玩家是 11.5、最常見的雜兵 NADEL 是 7.13 —— **Boss 比雜魚還慢一半**，
    玩家站著不動牠都要二十秒才走得到，一走動就永遠追不上。
    症狀在截圖上是「Boss 從頭到尾不在畫面裡」（血條在、東西看不到）。

    4.7 的錨點是 **WALZE ≈ NADEL**（1.5 × 4.7 = 7.05 對 7.13）：
    地面壓迫型的 Boss 至少要跟得上雜兵的行軍速度，否則牠出場只是一個
    在遠處放彈幕的血條。慢的那幾隻（MUTTER 2.8、KATHEDRALE 3.3）維持慢，
    牠們的威脅本來就是射程與召喚，但**慢也要慢在畫面裡**（見 `sim/boss-ai.ts`
    的交戰帶：那個帶子一律訂在鏡頭看得到的範圍內）。 */
export var BOSS_SPD_K = 4.7;

/** 戰場上 BOSS 的體型：**大隻的要壓縮，但順序要保住**。

    ⚠️ **這是量出來才發現非做不可的一條。** 體型原本一律套 `BODY_K`（×2），
    與小怪、與圖鑑同一把尺 —— 而戰場鏡頭（b-mid：高 26、距 25、FOV 46）
    在 Boss 那個距離上的可視高度只有 22 個世界單位。實測（`tools/boss-frame-probe.js`
    量包圍盒的 NDC 佔比，1.0 = 整個畫面高）：

      walze 6.6 → 0.65 ✅　zenit 9.2 → 1.28　mutter 15.2 → 1.26
      kathedrale 18 → 1.66 ❌　kaiser 48 → 1.83 ❌（十次取樣 0–1 次完整入鏡）

    截圖上的症狀不是「被切掉一點」，是**整隻讀不出來**：MUTTER 在畫面裡是
    一根貫穿上緣的白柱子，KATHEDRALE 與 KAISER 只剩一個角卡在畫面邊。

    「把鏡頭再退遠」不是解 —— 要框得下 48 單位得退到 56 單位外，
    那時玩家機體只剩畫面高度的 1/12，正好是使用者 2026-08-02 明講不要的
    「看著一個棋子」。所以**縮的是 BOSS 不是鏡頭**。

    ## 為什麼是指數飽和不是硬夾（2026-08-03 第二輪，使用者拍板）

    第一版是 `min(15, 拐點 + 超出量 × 35%)`。硬夾有兩個問題：

    **一、上限以上的隻數會並列。** 圖鑑 28 隻裡有 6 隻的 raw 超過 15
    （WELTACHSE h=48、KAISER 24、REQUIEM 22、NICHTS 20、CHIMÄRE 16、TURM 14），
    夾完全部 15.00 —— WELTACHSE 是 KAISER 的兩倍高，畫面上卻一樣。
    註解自己寫著「軟性壓縮是為了保住順序」，那個保證在飽和區早就失效了。

    **二、KAISER 被壓過頭。** 實測（`tools/boss-frame-probe.js`，各跑三輪取中位數）：

      硬夾 15  （KAISER 15.0）：入鏡 7/8/10　佔畫面高 **31%**
      硬夾 20  （KAISER 20.0）：入鏡 8/8/8 　佔畫面高 **63%**
      本版飽和（KAISER 18.0）：入鏡 8/7/8 　佔畫面高 **50%**

    入鏡率同樣達標（判準 ≥0.8），但畫面佔比差一倍 —— 最終 Boss 在 31% 下
    讀起來只是「比較大的東西」。⚠️ 鏡頭的 `CAM.bossZoom` 是**跟著 worldH
    線性拉遠**的，所以放大體型有一部分會被鏡頭自己抵消；15→20 的淨效果
    要實測才知道，不能用比例心算。

    改成**指數飽和**：拐點以上以 35% 的初始斜率漸近到 `BOSS_H_MAX`，
    永遠到不了、也永遠嚴格遞增。順序因此在任何高度都保得住：

      mutter 11.7　kathedrale 12.5　turm 14.9　chimare 15.7
      nichts 17.0　requiem 17.6　kaiser 18.0　weltachse 21.0

    KAISER 從 15 變 18（玩家機體的 3.6 倍），而 WELTACHSE 之後若排進戰場
    仍然明顯更大。**圖鑑用同一個函式**（`render/codex-model.ts` 與
    `ui/codex-bars.ts`），所以圖鑑裡的大小＝戰場上的大小，
    這是使用者 2026-08-03 拍板的。

    要再大就調 `BOSS_H_MAX`（漸近上界，不是任何一隻的實際高度）：
    22 → KAISER 18.0、26 → 19.0。調完**一定要重跑 frame probe**，
    因為鏡頭會自己抵消掉一部分（見上）。 */
export var BOSS_H_KNEE = 10, BOSS_H_SLOPE = 0.35, BOSS_H_MAX = 22;

/** 戰場上的體型倍率。小於 `BODY_K` 只會發生在超過拐點的大型 BOSS 身上。

    **圖鑑也讀這個函式** —— 兩邊同一把尺是使用者拍板的，改這裡兩邊一起動。 */
export function bossFieldScale(h) {
  var raw = h * BODY_K;
  if (raw <= BOSS_H_KNEE) return BODY_K;
  /* 指數飽和。在 raw = 拐點 處的斜率剛好是 `BOSS_H_SLOPE`（與線性版接得上），
     之後逐漸放緩、漸近 `BOSS_H_MAX` 但永不到達 —— 所以嚴格遞增、不會並列。 */
  var span = BOSS_H_MAX - BOSS_H_KNEE;
  var eff = BOSS_H_KNEE + span * (1 - Math.exp(-BOSS_H_SLOPE * (raw - BOSS_H_KNEE) / span));
  return eff / h;
}

/** 威脅色 → 十六進位。 */
function hex(t) { return (TINT[t] || TINT.red).hex; }

/* ---------- 弱點（規格 §5.2 的 `breakName` 只有兩隻有） ----------

   `at` 是 rig 的**局部座標**（未乘 `BODY_K`；標記掛在 root 底下，
   root 自己已經縮放過了）。`ang`/`arc` 是命中扇形，`hp` 是佔 Boss 滿血的比例。 */
var WEAK = {
  walze: [
    /* 左前腿：`walzeLeg(-1, 1)` 的髖在 (−1.16, 0, 0.98) → 方位角 −0.87。
       打掉之後腿拖著走（`rig.legs[1].dead`），移速砍半。 */
    { id: 'leg', n: '左前腿', ang: -0.87, arc: 0.62, hp: 0.15, at: [-1.16, 1.20, 0.98], r: 0.62 },
    /* 砲塔核心在砲塔的**後方**（tur 局部 (0, 0.66, −0.26)）——
       所以它是「繞到背後才打得到」。打掉之後直射砲降一半威力。 */
    { id: 'core', n: '砲塔核心', ang: Math.PI, arc: 0.70, hp: 0.15, at: [0, 2.94, -0.90], r: 0.70 }
  ],
  zenit: [
    /* 四具垂莢裡的左右兩具（`zenitPod(±HP)`，半徑 1.78）。
       各打掉一具就少一組彈幕 —— 這一場的「打亮的地方」給的是**火力減免**。 */
    { id: 'podR', n: '右砲莢', ang: Math.PI / 2, arc: 0.60, hp: 0.13, at: [1.78, 1.64, 0], r: 0.70 },
    { id: 'podL', n: '左砲莢', ang: -Math.PI / 2, arc: 0.60, hp: 0.13, at: [-1.78, 1.64, 0], r: 0.70 }
  ]
};

/* ---------- 2026-08-06：血量與衝擊上限整排重訂 ----------

   使用者實玩：「boss 踉蹌值太低，很容易進踉蹌，血量也不夠」。
   `flow.test.ts` 每次都會把實測時長與 `want` 並排印出來，而那一行
   一直在說同一件事 —— 只是沒有人回來調：

     Boss          出現   舊 hp   want   該時刻 DPS   舊的實際秒數
     walze         5:00    9,000    45s        549       16.4
     zenit         9:00   12,000    50s      2,394        5.0
     mutter       13:00   30,000    60s     11,340        2.6
     kathedrale   16:00   26,000    70s     14,515        1.8
     kaiser       20:00   58,000   110s     20,086        2.9

   檔頭訂的是「bot 的時間 ≈ `want` 的一半」（真人會走位、會躲、火力沒那麼滿），
   所以目標是 22/25/30/35/55 秒 —— 實際差了 3 到 20 倍。

   新血量 ＝ **該時刻 DPS × (want ÷ 2) × 0.75**。打 0.75 是因為報表的 DPS
   是「對整場」的輸出，範圍武裝有一部分打在雜兵身上而不是 Boss。
   DPS 來自十顆種子的中位數（`.claude/notes/metrics-latest.md` 第 4 區）。

   ⚠️ **衝擊上限（`imp`）一律 ×3。** 舊值是照「血量」的階梯訂的，而血量
   一次跳了 5–15 倍 —— 不跟著動的話 Boss 會從「很容易踉蹌」變成
   「從頭到尾都在踉蹌」。3 倍是估的，要用 `pnpm metrics` 的踉蹌次數校正：
   一場 Boss 戰踉蹌 2–3 次是設計意圖（`sim/impact.ts` 檔頭），
   一分鐘 17 次是噪音（lessons 2026-08-01 記過同型）。 */

/** 每一隻的戰鬥數值。欄位語意見檔頭。 */
export var BOSS_STATS: Record<string, any> = {
  /* 5:00 · 紅 —— 教「打亮的地方」。四色裡唯一有接觸傷害的（規格 §2.3）。 */
  walze: {
    hp: 9000, imp: 630, dmg: 34, r: 3.4, y: 2.6, score: 60,
    /* 砲塔是橙的（規格：紅＝牠靠撞、橙＝那一發是直射）。
       直射的預警線因此畫橙色，不是機體的紅。 */
    want: 45, dilute: 0.28, accent2: hex('amb')
  },
  /* 9:00 · 紫 —— 落點彈幕。浮空，不吃地形。 */
  zenit: {
    hp: 45000, imp: 750, dmg: 0, r: 3.6, y: 3.2, score: 85,
    want: 50, dilute: 0.28
  },
  /* 13:00 · 天藍 —— 生成。召喚時進護盾，清完才破盾（設計文件 §3）。
     P2 轉紫：核心直射。 */
  mutter: {
    hp: 255000, imp: 870, dmg: 0, r: 4.4, y: 4.4, score: 110,
    want: 60, dilute: 0.32, accent2: hex('vio')
  },
  /* 16:00 · 橙 —— 直射砲會**把掩體轟掉**，這一場教的就是那件事。
     P2 轉紫：核心露出、加一圈環形彈幕。 */
  kathedrale: {
    hp: 380000, imp: 990, dmg: 0, r: 6.2, y: 4.0, score: 140,
    want: 70, dilute: 0.32, accent2: hex('vio')
  },
  /* **20:00 · 橙 · 最終** —— 使用者 2026-08-03 指定 KAISER。
     2026-08-06 起牠在 20:00 整降臨，而且**打倒牠才通關**
     （`data/boss-roster.ts`、`sim/stage-flow.ts`）。
     三階段：立姿砲擊 → 甩甲跪地變固定砲台 → 上半身脫離浮空。 */
  kaiser: {
    /* `y` 跟著體型走（2026-08-03 從 15 變 18.0，6.4 × 1.20 = 7.7）——
       它是彈道與傷害數字的基準高度（`eShoot` 用 `T.y + 1.6`），
       不跟著抬的話砲口會從機體腰下噴出來、傷害數字飄在肚子上。 */
    hp: 830000, imp: 1290, dmg: 0, r: 5.0, y: 7.7, score: 280,
    want: 110, dilute: 0.38, accent2: hex('vio')
  }
};

/** 階段推進的血量門檻（由高到低）。兩階段一個門檻、三階段兩個。 */
export function bossPhases(stages) {
  return stages >= 3 ? [0.66, 0.33] : [0.5];
}

/** `ETYPE` 形狀的 Boss 型別表。

    ⚠️ **不要把 `data/bosses.ts` 的 `fam` 帶過來。** 那是剪影家族（圖鑑分組用），
    而 `makeEnemy()` 拿 `T.fam` 當「這是小怪」的分流旗標 —— 帶過去的話
    KATHEDRALE 會被當成 WALL 家族的雜魚，走 part 表那條路直接壞掉。 */
export var BOSS_TYPES: Record<string, any> = {};
for (var _id in BOSS_STATS) {
  var B = BOSS_BY_ID[_id], S = BOSS_STATS[_id];
  var st = B.stages || 2;
  BOSS_TYPES[_id] = {
    id: B.id, code: B.code, zh: B.zh, tint: B.tint, boss: true,
    /* `bossRig` = 走 `entities/boss-rig.ts` 那條路（真階層 + `upd()`）。
       舊的四隻 Boss（core/gemini/monolith/final）在 B2 整批退場，
       所以這個旗標現在等同於 `boss`，留著是給之後可能的舊式 Boss 用。 */
    bossRig: true,
    bn: B.code + ' ' + B.zh,
    /* 尺寸是**敵人那把尺**（未乘 `BODY_K`），要世界單位自己乘 —— 與小怪同一條規矩。 */
    h: B.h, w: B.w, bodyK: bossFieldScale(B.h), worldH: B.h * bossFieldScale(B.h),
    fly: B.fly ? 1 : 0,
    build: B.build, upd: B.upd, stages: st, phases: bossPhases(st),
    gait: B.gait, turn: B.turn, tTel: B.tTel, tAtk: B.tAtk, tRec: B.tRec,
    /* 換算過的（與小怪同兩個係數，見 `data/enemies.ts`）。 */
    spd: B.spd * BOSS_SPD_K, rng: B.rng * RNG_K, spd0: B.spd, rng0: B.rng,
    hp: S.hp, imp: S.imp, dmg: S.dmg, r: S.r, y: S.y, xp: 0, score: S.score,
    want: S.want, dilute: S.dilute,
    accent: hex(B.tint), accent2: S.accent2 || hex(B.tint),
    /* `body` 只被死亡碎片讀（碎片是獨立粒子池，不吃 Boss 的材質）。 */
    body: 0x59616f,
    parts: WEAK[_id] || null,
    cn: B.code + ' ' + B.zh
  };
}
